import React from "react"
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material"
import { Link } from "react-router-dom"
import InstagramIcon from "@mui/icons-material/Instagram"

const NAV_LINKS = [
  { label: "About",   to: "/about" },
  { label: "Gallery", to: "/gallery" },
  { label: "FAQ",     to: "/faq" },
  { label: "Contact", to: "/contact" },
]

const Footer = ({ minimal = false }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  if (minimal) {
    return (
      <Box
        component="footer"
        sx={{
          backgroundColor: "#0a0f14",
          borderTop: "1px solid #1e2d3d",
          px: 3,
          py: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Link to="/" style={{ textDecoration: "none" }}>
          <Typography
            sx={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "#8a9bb0",
              transition: "color 0.2s ease",
              "&:hover": { color: "#c9a84c" },
            }}
          >
            Home
          </Typography>
        </Link>
        <Box
          component="a"
          href="https://www.instagram.com/marlin.surf/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#8a9bb0",
            textDecoration: "none",
            transition: "color 0.2s ease",
            "&:hover": { color: "#c9a84c" },
          }}
        >
          <InstagramIcon sx={{ fontSize: "1.1rem" }} />
          <Typography
            sx={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.1em",
            }}
          >
            @marlin.surf
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: "0.65rem",
            fontWeight: 300,
            letterSpacing: "0.08em",
            color: "#8a9bb0",
            opacity: 0.6,
          }}
        >
          © {new Date().getFullYear()} Marlin Surfboards
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {[
            { label: "Privacy", to: "/privacy" },
            { label: "Cookies", to: "/cookies" },
          ].map(({ label, to }) => (
            <Link key={label} to={to} style={{ textDecoration: "none" }}>
              <Typography
                sx={{
                  fontFamily: "'Josefin Sans', sans-serif",
                  fontSize: "0.62rem",
                  fontWeight: 300,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#8a9bb0",
                  opacity: 0.5,
                  transition: "opacity 0.2s ease",
                  "&:hover": { opacity: 0.9 },
                }}
              >
                {label}
              </Typography>
            </Link>
          ))}
        </Box>
      </Box>
    )
  }

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: "#0a0f14",
        borderTop: "1px solid #1e2d3d",
        px: { xs: 3, md: 6 },
        py: { xs: 5, md: 6 },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
          gap: { xs: 4, md: 2 },
          maxWidth: 1200,
          mx: "auto",
          alignItems: "start",
        }}
      >
        {/* ── Left: Logo + Tagline ───────────────────────────────────────── */}
        <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Kenilla', serif",
              fontSize: { xs: "1.6rem", md: "1.8rem" },
              fontWeight: 400,
              letterSpacing: "0.12em",
              color: "#f0ede8",
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            MARLIN
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: "0.68rem",
              fontWeight: 300,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#8a9bb0",
              display: "block",
            }}
          >
            Hand Shaped. Ocean Tested.
          </Typography>
        </Box>

        {/* ── Center: Nav Links ──────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {NAV_LINKS.map(({ label, to }) => (
            <Link
              key={label}
              to={to}
              style={{ textDecoration: "none" }}
            >
              <Typography
                sx={{
                  fontFamily: "'Josefin Sans', sans-serif",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "#8a9bb0",
                  transition: "color 0.2s ease",
                  "&:hover": {
                    color: "#c9a84c",
                  },
                }}
              >
                {label}
              </Typography>
            </Link>
          ))}
        </Box>

        {/* ── Right: Instagram + Copyright ───────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile ? "center" : "flex-end",
            gap: 1.5,
          }}
        >
          <Box
            component="a"
            href="https://www.instagram.com/marlin.surf/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#8a9bb0",
              textDecoration: "none",
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#c9a84c",
              },
            }}
          >
            <InstagramIcon sx={{ fontSize: "1.1rem" }} />
            <Typography
              sx={{
                fontFamily: "'Josefin Sans', sans-serif",
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.1em",
              }}
            >
              @marlin.surf
            </Typography>
          </Box>

          <Typography
            sx={{
              fontFamily: "'Josefin Sans', sans-serif",
              fontSize: "0.65rem",
              fontWeight: 300,
              letterSpacing: "0.08em",
              color: "#8a9bb0",
              opacity: 0.6,
            }}
          >
            © {new Date().getFullYear()} Marlin Surfboards
          </Typography>

          {/* Legal links */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {[
              { label: "Privacy", to: "/privacy" },
              { label: "Cookies", to: "/cookies" },
            ].map(({ label, to }) => (
              <Link key={label} to={to} style={{ textDecoration: "none" }}>
                <Typography
                  sx={{
                    fontFamily: "'Josefin Sans', sans-serif",
                    fontSize: "0.62rem",
                    fontWeight: 300,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#8a9bb0",
                    opacity: 0.5,
                    transition: "opacity 0.2s ease",
                    "&:hover": {
                      opacity: 0.9,
                    },
                  }}
                >
                  {label}
                </Typography>
              </Link>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default Footer
